import { useMediaQuery } from 'react-responsive';
import {
  Box,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModalStyle from 'src/components/modal/ModalStyle';
import { useContext } from 'react';
import AssetTableContext from 'src/store/asset-table-context';

const accordionStyle = {
  summary: {
    '& .MuiAccordionSummary-content': {
      justifyContent: 'center',
    },
    fontWeight: 'bold',
  },
  details: { display: 'flex', justifyContent: 'left' },
};

const HelpModal = () => {
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });
  const { helpModal } = useContext(AssetTableContext);
  const { helpModalController } = helpModal;

  return (
    <Modal open={helpModalController.open} onClose={helpModalController.closeModal}>
      <Box sx={ModalStyle}>
        <Accordion>
          <AccordionSummary sx={accordionStyle.summary} expandIcon={<ExpandMoreIcon />}>
            Step 1{isDesktop && ' - Download'}
          </AccordionSummary>
          <AccordionDetails sx={accordionStyle.details}>
            <ul>
              <li>Click &quot;Asset Spreadsheet&quot; to download the asset excel file.</li>
              <li>This will download an excel document of your current assets to be modified</li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary sx={accordionStyle.summary} expandIcon={<ExpandMoreIcon />}>
            Step 2 {isDesktop && ' - Update'}
          </AccordionSummary>
          <AccordionDetails sx={accordionStyle.details}>
            <Box>
              <p>
                You can make the necessary changes to the spreadsheet by adding and deleting items.
                Please keep the spreadsheet in the same layout as when you downloaded it. The
                following is the data that should be entered for each new item:
              </p>
              <ul>
                <li>Column A - Item Description (77 characters maximum)</li>
                <li>Column B - Property Type Code. (2 digits maximum)</li>
                <li>Column C - Year of Acquisition (4 digits, year only - no dates)</li>
                <li>
                  Column D - Acquisition Cost (numbers only, rounded to whole dollars - per item)
                </li>
                <li>Column E - Quantity (numbers only)</li>
                <li>Column F - Lessee Name (70 characters maximum)</li>
                <li>Column G - Lessee Address ( 80 characters maximum)</li>
                <li>Column H - Lessee City (40 characters maximum)</li>
                <li>Column I - Rec_Number (Do Not Edit)</li>
                <li>Column J - Item_No (Do Not Edit)</li>
              </ul>
              <p>
                Please do not leave columns A thru E empty. Columns F, G, and H are only used if you
                lease this item to someone else in Utah County, otherwise please leave these columns
                blank. <b>Columns I and J should not be changed</b>.
              </p>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary sx={accordionStyle.summary} expandIcon={<ExpandMoreIcon />}>
            Step 3{isDesktop && ' - Save'}
          </AccordionSummary>
          <AccordionDetails sx={accordionStyle.details}>
            <ul>
              <li>Save the file</li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary sx={accordionStyle.summary} expandIcon={<ExpandMoreIcon />}>
            Step 4{isDesktop && ' - Upload Spreadsheet'}
          </AccordionSummary>
          <AccordionDetails sx={accordionStyle.details}>
            <ul>
              <li>
                After you have completed your changes and saved the spreadsheet file, upload your
                file by clicking {'"Asset Spreadsheet"'}. A modal will appear where you can drag and
                drop, or navigate to your file, for upload. Make sure you click &quot;Upload&quot;
                when your desired file is selected.
              </li>
              <li>
                In uploading the excel document the format must match the downloaded format. Please
                do not make changes to any of the column names. Additionally, columns <b>G</b>,{' '}
                <b>K</b>, and <b>L</b> are for internal use only do not modify.
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Divider variant="middle" sx={{ my: 2 }} />
        <Accordion>
          <AccordionSummary sx={accordionStyle.summary} expandIcon={<ExpandMoreIcon />}>
            Acquisition Cost
          </AccordionSummary>
          <AccordionDetails sx={accordionStyle.details}>
            <Box>
              <p>
                <strong>Acquisition Cost defined</strong>{' '}
                <i>
                  <u>(Utah Code Ann. 59-2-108)</u>
                </i>
                : Acquisition Cost must include all costs required to put an item into service.
                These costs include the following costs:
              </p>
              <ul>
                <li>The purchase price for a new or used item.</li>
                <li>
                  The cost of freight and shipping. Shipping costs includes, loading at origin,
                  unloading at destination, crating, skidding and other applicable costs.
                </li>
                <li>
                  The cost of installation, engineering, erection, or assembly. Erection and
                  assembly includes foundations, pilings, utility connections, and any other costs.
                </li>
                <li>Sales and use taxes.</li>
                <li>
                  Any other costs related to putting a personal property item into service are to be
                  included in acquisition costs.
                </li>
              </ul>
              <p>
                Item of Taxable Tangible Personal Property is defined as{' '}
                <i>
                  <u>(Admin Rule R884-24P-33(1) (d)</u>
                </i>
                :
              </p>
              <ul>
                <li>
                  A piece of equipment, machinery, furniture, or other piece of tangible personal
                  property that is functioning at its highest and best use for the purpose it was
                  designed and constructed and is generally capable of performing that function
                  without being combined with other items of personal property. An item of taxable
                  tangible personal property is not an individual component part of a piece of
                  machinery or equipment, but the piece of machinery or equipment. For example, a
                  fully functioning computer is an item of taxable tangible personal property but
                  the motherboard, hard drive, tower or sound card are not.
                </li>
              </ul>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary sx={accordionStyle.summary} expandIcon={<ExpandMoreIcon />}>
            Residential Exemptions
          </AccordionSummary>
          <AccordionDetails sx={accordionStyle.details}>
            <Box>
              <p>
                <strong>
                  Primary Residential Exemptions for Personal Property in Qualified Residential
                  Property
                </strong>{' '}
                <i>
                  <u>(Utah Code Ann. 59-2-103)</u>
                </i>{' '}
              </p>
              <p>
                Residential Rental Property household furnishings, furniture, and equipment qualify
                for the primary residential exemption under 59-2-103 (30) (a) if:
              </p>
              <ul>
                <li>
                  It is used{' '}
                  <strong>
                    exclusively within a dwelling unit that is the primary residence of a tenant.
                  </strong>
                </li>
                <li>
                  Owned by the owner of the dwelling unit that is the primary residence of a tenant.
                </li>
              </ul>
              <p>
                The exemption does <strong>not</strong> include property used for transient
                residential use
                <i>
                  <u>(59-2-103 (32) (B) (ii)</u>
                </i>
                .
              </p>
              <ul>
                <li>Motels</li>
                <li>Hotels</li>
                <li>Time Shares</li>
                <li>Rehabilitation Centers</li>
                <li>Residential Property used in a short term rental pool</li>
                <li>
                  Property not used exclusively inside the dwelling unit such as club house
                  furniture and fixtures, exercise facilities, pool equipment and furniture.
                </li>
              </ul>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Grid container spacing={2} sx={{ justifyContent: 'end' }}>
          <Grid item>
            <Button variant="contained" data-cy="close" onClick={helpModalController.closeModal}>
              Close
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default HelpModal;
